import {
  RESOLUTION_BAND_IDS,
  type ResolutionBand,
  type ResolutionBandId,
  type ResolutionSummary,
} from '@/services/api/entity-resolution-assistant'
import {
  bulkEligibleGroups,
  identityChecksAvailable,
  parseIdentityChecks,
} from './resolution-identity'
import {
  isResolutionCount as count,
  isResolutionScore,
  isResolutionRecord as record,
} from './resolution-values'

const score = (value: unknown): boolean => value === null || isResolutionScore(value)

function isBand(value: unknown): value is ResolutionBand {
  return (
    record(value) &&
    RESOLUTION_BAND_IDS.includes(value.id as ResolutionBandId) &&
    typeof value.label === 'string' &&
    score(value.min_score) &&
    score(value.max_score) &&
    count(value.group_count) &&
    count(value.candidate_count) &&
    typeof value.percentage === 'number' &&
    Number.isFinite(value.percentage) &&
    value.percentage >= 0 &&
    value.percentage <= 100
  )
}

/** Never render merge controls for malformed tool output or another case's snapshot. */
export function parseResolutionSummary(raw: unknown, caseId: string): ResolutionSummary | null {
  try {
    const value: unknown = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (
      !record(value) ||
      value.status === 'error' ||
      value.case_id !== caseId ||
      typeof value.snapshot_id !== 'string' ||
      !value.snapshot_id ||
      !count(value.total_groups) ||
      !count(value.total_candidates) ||
      typeof value.score_basis !== 'string' ||
      !Array.isArray(value.bands) ||
      !value.bands.every(isBand)
    )
      return null
    const bands = value.bands
    if (
      bands.length !== RESOLUTION_BAND_IDS.length ||
      new Set(bands.map(b => b.id)).size !== bands.length ||
      bands.reduce((sum, band) => sum + band.group_count, 0) !== value.total_groups
    )
      return null
    const identityChecks = parseIdentityChecks(value.identity_checks, bands, value.total_groups)
    if (identityChecks === null) return null
    return {
      case_id: caseId,
      snapshot_id: value.snapshot_id,
      total_groups: value.total_groups,
      total_candidates: value.total_candidates,
      score_basis: value.score_basis,
      bands,
      ...(identityChecks && { identity_checks: identityChecks }),
    }
  } catch {
    return null
  }
}

export function selectedResolutionBands(summary: ResolutionSummary, selected: ResolutionBandId[]) {
  if (!identityChecksAvailable(summary)) return []
  return summary.bands.filter(
    band => band.id !== 'unknown' && bulkEligibleGroups(band) > 0 && selected.includes(band.id)
  )
}

/** Rich snapshots travel in the artifact; older messages kept them in the result. */
export function parseResolutionToolSummary(result: unknown, artifact: unknown, caseId: string) {
  return parseResolutionSummary(
    record(artifact) ? (artifact.resolution_summary ?? result) : result,
    caseId
  )
}
